/**
 * Neuronic ad tag — bootstrap entry point.
 *
 * Integration (all environments):
 *   <script src="tag.js" async></script>
 *   <script>(window.__neuronic = window.__neuronic || []).push({ placementId: 'abc123' })</script>
 *
 * Bootstrap sequence:
 *   1. Capture currentScript immediately — only available during top-level execution
 *   2. Guard against double-load (cached script, duplicate includes)
 *   3. Read dev-only URL params (e.g. ?neuronic_force_bid=1)
 *   4. Resolve consent per placement with in-flight de-dupe
 *   5. Drain pre-load queue in parallel; replace window namespace with live-processing object
 */

import logger from './debug.js';
import { collectDeviceSua } from './deviceSignals.js';
import { resolveTagPrivacy, summarizePrivacy } from './privacy.js';
import { getRenderEnv } from './renderEnv.js';
import { handlePlacement } from './placement.js';

const log = logger.for('bootstrap');

// Must be captured at top level — browser clears document.currentScript after initial execution
const bootstrapScript = document.currentScript;

// Dev-only URL params — omitted from normal production requests.
//   ?neuronic_force_bid=1       forces TestBidderTask to inject a synthetic winning bid
//   ?neuronic_endpoint=<url>    overrides the baked-in ADTAG_ENDPOINT at runtime (live E2E testing)
const params           = new URLSearchParams(window.location.search);
const forceBid         = params.has('neuronic_force_bid');
const endpointOverride = params.get('neuronic_endpoint') || null;

if (forceBid)
  log.warn('force_bid active — synthetic bids will be injected (dev only)');

if (endpointOverride)
  log.warn('endpoint override active', endpointOverride);

const existing = window[__NAMESPACE__];

if (existing?.loaded) {
  log.warn('already loaded — skipping duplicate bootstrap');
} else {
  init(existing);
}

// ─────────────────────────────────────────────────────────────────────────────

function init(existingQueue) {
  const queue = Array.isArray(existingQueue) ? existingQueue : [];

  log.info('bootstrap', { queueLength: queue.length, ns: __NAMESPACE__ });

  const renderEnv = getRenderEnv();
  const privacyPromise = resolveTagPrivacy();
  const deviceSuaPromise = collectDeviceSua();

  privacyPromise.then(privacy => {
    log.debug('privacy ready', summarizePrivacy(privacy.snapshot()));
    privacy.onChange(snapshot => log.debug('privacy changed', summarizePrivacy(snapshot)));
  }).catch(err => log.error('privacy initialization failed', err));

  log.debug('render env', renderEnv);

  // Replace the window array with a live-processing object.
  // Any push() calls after this point are handled immediately.
  // `document.currentScript` during a synchronous push() IS the publisher's
  // push snippet — the element that marks the slot. Captured here (it's
  // gone by the time async work runs) and used as the preferred in_place
  // anchor; loader integrations put tag.js in <head>, where the bootstrap
  // script is useless as an anchor. Queued entries pushed before load
  // can't capture it — findPushSite falls back to a snippet text scan.
  window[__NAMESPACE__] = {
    loaded: true,
    push: entry => handlePlacement(entry, renderEnv, bootstrapScript, privacyPromise, deviceSuaPromise, forceBid, endpointOverride, document.currentScript),
  };

  // Fire all queued placements in parallel — each is fully independent
  Promise.all(queue.map(entry => handlePlacement(entry, renderEnv, bootstrapScript, privacyPromise, deviceSuaPromise, forceBid, endpointOverride)));
}
